import React, { useEffect, useState, useCallback, useRef, lazy, Suspense } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import RecipePlanEditor from "@/components/RecipePlanEditor";
import { emptyPlan, calculateQuantities, validateRecipe } from "../../../../shared/compost-plan";
import { STAGING_MAP_COLOR, yardRoleLabel } from "../../../../shared/yard-staging";
import { apiFetch } from "@/lib/api-fetch";
import { GoogleMap, useLoadScript, MarkerF, PolylineF, InfoWindowF, CircleF } from "@react-google-maps/api";
import {
  Thermometer,
  RotateCcw,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Plus,
  ChevronRight,
  X,
  Layers,
  Flame,
  Gauge,
  MapPin,
  Map,
  List,
  MoreVertical,
  Trash2,
} from "lucide-react";
const WindrowLinePicker = lazy(() => import("@/components/WindrowLinePicker"));
const WindrowCirclePicker = lazy(() => import("@/components/WindrowCirclePicker"));

const GOOGLE_MAPS_API_KEY = "AIzaSyDZWH9JxBDTkBOGkBhwV7Hjx1F45eTyYIo";

// Parker Dairy plant: 34°10'42.48"N 112°47'17.94"W
const DEFAULT_CENTER = { lat: 34.178467, lng: -112.788317 };

type Windrow = {
  id: number;
  row_id: string;
  name: string;
  location: string | null;
  status: string;
  start_date: string;
  shape?: "line" | "pile";
  yard_role?: "cooking" | "staging" | "curing";
  length: number | null;
  width: number | null;
  cubic_yards: number | null;
  tonnage: number | null;
  recipe: string | null;
  notes: string | null;
  latitude: number | null;
  longitude: number | null;
  end_latitude: number | null;
  end_longitude: number | null;
  color: string | null;
  last_temp: number | null;
  last_temp_at: string | null;
  last_temp_by: string | null;
  temp_count: number;
  turn_count: number;
  last_turn_at: string | null;
};

function getLineColor(w: Windrow): string {
  if ((w.yard_role || "cooking") === "staging") return w.color && /^#[0-9A-Fa-f]{6}$/.test(w.color) ? w.color : STAGING_MAP_COLOR;
  return w.color && /^#[0-9A-Fa-f]{6}$/.test(w.color) ? w.color : (STATUS_LINE_COLORS[w.status] || "#3d8f3d");
}

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: typeof Clock }> = {
  active: { label: "Active", color: "bg-forest-100 text-forest-700 dark:bg-forest-900/30 dark:text-forest-400", icon: Clock },
  cooking_complete: { label: "Cooking Complete", color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400", icon: CheckCircle2 },
  ready_for_pickup: { label: "Ready for Pickup", color: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400", icon: CheckCircle2 },
  removed: { label: "Removed", color: "bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400", icon: CheckCircle2 },
};

const STATUS_LINE_COLORS: Record<string, string> = {
  active: "#3d8f3d",
  cooking_complete: "#3b82f6",
  ready_for_pickup: "#8b5cf6",
  removed: "#9ca3af",
};

/** Minimal list-row status marker (no tinted pills). */
const STATUS_INDICATOR_DOT: Record<string, string> = {
  active: "bg-emerald-600 dark:bg-emerald-400",
  cooking_complete: "bg-sky-600 dark:bg-sky-400",
  ready_for_pickup: "bg-violet-600 dark:bg-violet-400",
  removed: "bg-neutral-400 dark:bg-neutral-500",
};

/**
 * Pile -- side-view heap (filled base + crown). `simple` for list cells; full adds ground + ridge for buttons/legend.
 */
function PileMoundIcon({
  className,
  size = 16,
  strokeWidth = 2,
  simple = false,
}: {
  className?: string;
  size?: number;
  strokeWidth?: number;
  simple?: boolean;
}) {
  const sw = simple ? Math.min(strokeWidth, 1.9) : strokeWidth;
  const crown = "M5.25 18.35Q12 6.25 18.75 18.35";
  const body =
    "M5.25 18.35Q12 6.25 18.75 18.35L18.55 19.12H5.45L5.25 18.35Z";

  if (simple) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={className}
        aria-hidden
      >
        <path d={body} fill="currentColor" fillOpacity={0.18} />
        <path
          d={crown}
          fill="none"
          stroke="currentColor"
          strokeWidth={sw}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  const groundW = Math.max(1.35, sw * 0.55);
  const ridgeW = Math.max(1.05, sw * 0.42);

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden
    >
      <path
        d="M2.5 20.85h19"
        stroke="currentColor"
        strokeWidth={groundW}
        strokeLinecap="round"
        opacity={0.4}
      />
      <path d={body} fill="currentColor" fillOpacity={0.22} />
      <path
        d="M8.1 14.6Q12 10.85 15.9 14.6"
        stroke="currentColor"
        strokeWidth={ridgeW}
        strokeLinecap="round"
        opacity={0.55}
      />
      <path
        d={crown}
        fill="none"
        stroke="currentColor"
        strokeWidth={sw}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Windrow -- side-view trapezoidal cross-section (peaked ridge with flat top).
 * Visually distinct from the rounded pile dome.
 */
function WindrowStripeIcon({
  className,
  size = 24,
  simple = false,
}: {
  className?: string;
  size?: number;
  simple?: boolean;
}) {
  if (simple) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={className}
        aria-hidden
      >
        <path
          d="M3.5 19 L8.5 9.5 L15.5 9.5 L20.5 19"
          fill="currentColor"
          fillOpacity={0.14}
        />
        <path
          d="M3.5 19 L8.5 9.5 L15.5 9.5 L20.5 19"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.9}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden
    >
      <path
        d="M2.5 20.85h19"
        stroke="currentColor"
        strokeWidth={1.35}
        strokeLinecap="round"
        opacity={0.4}
      />
      <path
        d="M3.5 19 L8.5 9 L15.5 9 L20.5 19 Z"
        fill="currentColor"
        fillOpacity={0.15}
      />
      <path
        d="M3.5 19 L8.5 9 L15.5 9 L20.5 19"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <path
        d="M10 9 L14 9"
        stroke="currentColor"
        strokeWidth={1.05}
        strokeLinecap="round"
        opacity={0.45}
      />
    </svg>
  );
}

function timeAgo(dateStr: string | null) {
  if (!dateStr) return "Never";
  const diff = Date.now() - new Date(dateStr).getTime();
  const hours = Math.floor(diff / 3600000);
  if (hours < 1) return "Just now";
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "1 day ago";
  return `${days} days ago`;
}

// Calculate offset point from lat/lng given distance in feet and bearing in degrees
function offsetPoint(lat: number, lng: number, distFeet: number, bearingDeg: number) {
  const FT_PER_DEG_LAT = 364000;
  const FT_PER_DEG_LNG = 364000 * Math.cos((lat * Math.PI) / 180);
  const bearingRad = (bearingDeg * Math.PI) / 180;
  return {
    lat: lat + (distFeet * Math.cos(bearingRad)) / FT_PER_DEG_LAT,
    lng: lng + (distFeet * Math.sin(bearingRad)) / FT_PER_DEG_LNG,
  };
}

function haversineMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// ============ SITE MAP COMPONENT ============
function WindrowSiteMap({ windrows }: { windrows: Windrow[]; token: string; onRefresh: () => void }) {
  const isLocalDemo = import.meta.env.VITE_LOCAL_DEMO === "true";
  const { isLoaded } = useLoadScript({ googleMapsApiKey: GOOGLE_MAPS_API_KEY });
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const navigate = useNavigate();

  const hasPosition = (w: Windrow) => w.latitude != null && w.longitude != null;
  const hasLine = (w: Windrow) => hasPosition(w) && w.end_latitude != null && w.end_longitude != null;
  const isPile = (w: Windrow) => w.shape === "pile";
  const isLineWindrow = (w: Windrow) => hasLine(w) && !isPile(w);
  const isPileWithCircle = (w: Windrow) => hasLine(w) && isPile(w);
  const positioned = windrows.filter(hasPosition);

  const center =
    positioned.length > 0
      ? {
          lat: positioned.reduce((s, w) => s + w.latitude!, 0) / positioned.length,
          lng: positioned.reduce((s, w) => s + w.longitude!, 0) / positioned.length,
        }
      : DEFAULT_CENTER;

  if (!isLoaded) {
    return (
      <div className="h-[550px] bg-card border border-border rounded-xl animate-pulse flex items-center justify-center">
        <p className="text-sm text-muted-foreground">Loading map...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Map (read-only overview) */}
      <div
        className="rounded-xl overflow-hidden border transition-all border-border"
        style={{ height: 550 }}
      >
        <GoogleMap
          center={center}
          zoom={positioned.length > 0 ? 18 : 16}
          onLoad={(map) => {
            if (!positioned.length) return;
            const bounds = new google.maps.LatLngBounds();
            for (const row of positioned) {
              bounds.extend({lat:row.latitude!,lng:row.longitude!});
              if (row.end_latitude != null && row.end_longitude != null) bounds.extend({lat:row.end_latitude,lng:row.end_longitude});
            }
            map.fitBounds(bounds, 65);
            // A closer, read-only camera for the local customer overview capture.
            if (isLocalDemo && new URLSearchParams(window.location.search).get('presentation') === '1') {
              google.maps.event.addListenerOnce(map, 'idle', () => {
                map.setZoom(18.6);
              });
            }
          }}
          mapContainerStyle={{ width: "100%", height: "100%" }}
          options={{
            isFractionalZoomEnabled: isLocalDemo,
            mapTypeId: "satellite",
            tilt: 0,
            mapTypeControl: true,
            mapTypeControlOptions: { position: 3 }, // TOP_RIGHT
            streetViewControl: false,
            fullscreenControl: true,
          }}
        >
          {/* Render all polylines (windrow lines only) */}
          {positioned.filter(isLineWindrow).map((w) => {
            const lineColor = getLineColor(w);
            const isHovered = hoveredId === w.id;
            return (
              <PolylineF
                key={`line-${w.id}`}
                path={[
                  { lat: w.latitude!, lng: w.longitude! },
                  { lat: w.end_latitude!, lng: w.end_longitude! },
                ]}
                options={{
                  strokeColor: lineColor,
                  strokeWeight: isHovered ? 8 : 6,
                  strokeOpacity: isHovered ? 1 : 0.9,
                  zIndex: isHovered ? 50 : 5,
                }}
                onClick={() => setSelectedId(selectedId === w.id ? null : w.id)}
                onMouseOver={() => setHoveredId(w.id)}
                onMouseOut={() => setHoveredId(null)}
              />
            );
          })}

          {/* Render piles as circles */}
          {positioned.filter(isPileWithCircle).map((w) => {
            const pileColor = getLineColor(w);
            const isHovered = hoveredId === w.id;
            const radiusM = haversineMeters(w.latitude!, w.longitude!, w.end_latitude!, w.end_longitude!);
            return (
              <CircleF
                key={`pile-${w.id}`}
                center={{ lat: w.latitude!, lng: w.longitude! }}
                radius={radiusM}
                options={{
                  fillColor: pileColor,
                  fillOpacity: isHovered ? 0.35 : 0.25,
                  strokeColor: pileColor,
                  strokeOpacity: isHovered ? 1 : 0.9,
                  strokeWeight: isHovered ? 5 : 4,
                  zIndex: isHovered ? 50 : 5,
                }}
                onClick={() => setSelectedId(selectedId === w.id ? null : w.id)}
                onMouseOver={() => setHoveredId(w.id)}
                onMouseOut={() => setHoveredId(null)}
              />
            );
          })}

          {/* Render start endpoint markers (read-only) -- lines only */}
          {positioned.filter(isLineWindrow).map((w) => (
            <MarkerF
              key={`start-${w.id}`}
              position={{ lat: w.latitude!, lng: w.longitude! }}
              draggable={false}
              icon={{
                path: google.maps.SymbolPath.CIRCLE,
                scale: 6,
                fillColor: getLineColor(w),
                fillOpacity: 1,
                strokeColor: "#fff",
                strokeWeight: 2,
              }}
              title={`${w.name} -- start`}
            />
          ))}

          {/* Render end endpoint markers (read-only) -- lines only */}
          {positioned.filter(isLineWindrow).map((w) => (
            <MarkerF
              key={`end-${w.id}`}
              position={{ lat: w.end_latitude!, lng: w.end_longitude! }}
              draggable={false}
              icon={{
                path: google.maps.SymbolPath.CIRCLE,
                scale: 6,
                fillColor: getLineColor(w),
                fillOpacity: 1,
                strokeColor: "#fff",
                strokeWeight: 2,
              }}
              title={`${w.name} -- end`}
            />
          ))}

          {/* Label markers at midpoints (lines) or center (piles) — all rendered, hovered gets higher z-index */}
          {positioned.map((w) => {
              const mid = hasLine(w)
                ? isPile(w)
                  ? { lat: w.latitude!, lng: w.longitude! }
                  : { lat: (w.latitude! + w.end_latitude!) / 2, lng: (w.longitude! + w.end_longitude!) / 2 }
                : { lat: w.latitude!, lng: w.longitude! };
              const color = getLineColor(w);
              const isHov = hoveredId === w.id;
              return (
                <MarkerF
                  key={`label-${w.id}`}
                  position={mid}
                  zIndex={isHov ? 100 : 10}
                  onClick={() => setSelectedId(selectedId === w.id ? null : w.id)}
                  onMouseOver={() => setHoveredId(w.id)}
                  onMouseOut={() => setHoveredId(null)}
                  icon={{
                    url: `data:image/svg+xml,${encodeURIComponent(
                      isHov
                        ? `<svg xmlns="http://www.w3.org/2000/svg" width="120" height="24"><rect rx="4" width="120" height="24" fill="${color}" opacity="1" stroke="white" stroke-width="1.5"/><text x="60" y="16" text-anchor="middle" fill="white" font-size="11" font-family="Arial" font-weight="bold">${w.name}</text></svg>`
                        : `<svg xmlns="http://www.w3.org/2000/svg" width="120" height="24"><rect rx="4" width="120" height="24" fill="${color}" opacity="0.9"/><text x="60" y="16" text-anchor="middle" fill="white" font-size="11" font-family="Arial" font-weight="bold">${w.name}</text></svg>`
                    )}`,
                    anchor: new google.maps.Point(60, 12),
                  }}
                  title={w.name}
                />
              );
            })}

          {/* Fallback markers for windrows with position but no line/radius (read-only) */}
          {positioned.filter((w) => !hasLine(w)).map((w) => (
            <MarkerF
              key={`pin-${w.id}`}
              position={{ lat: w.latitude!, lng: w.longitude! }}
              onClick={() => setSelectedId(selectedId === w.id ? null : w.id)}
              title={w.name}
            />
          ))}

          {/* Info window for selected windrow/pile */}
          {selectedId && (() => {
            const w = positioned.find((x) => x.id === selectedId);
            if (!w) return null;
            const mid = hasLine(w)
              ? isPile(w)
                ? { lat: w.latitude!, lng: w.longitude! }
                : { lat: (w.latitude! + w.end_latitude!) / 2, lng: (w.longitude! + w.end_longitude!) / 2 }
              : { lat: w.latitude!, lng: w.longitude! };
            const color = getLineColor(w);
            return (
              <InfoWindowF position={mid} onCloseClick={() => setSelectedId(null)}>
                <div style={{ padding: 4, minWidth: 200, maxWidth: 260, fontFamily: "Arial, sans-serif" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: color, flexShrink: 0 }} />
                    <h3 style={{ fontWeight: 700, fontSize: 15, color: "#111", margin: 0 }}>{w.name}</h3>
                  </div>
                  <p style={{ fontSize: 10, fontWeight: 600, color: "#444", margin: "0 0 4px", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                    {isPile(w) ? ((w.yard_role || "cooking") === "staging" ? "Staging" : "Pile") : "Windrow"}
                  </p>
                  <p style={{ fontSize: 11, color: "#666", margin: "0 0 6px" }}>
                    {w.row_id} · {(w.yard_role || "cooking") === "staging" ? "Staging" : (STATUS_CONFIG[w.status]?.label || w.status)}
                  </p>
                  {w.last_temp !== null && (
                    <div style={{
                      display: "flex", alignItems: "baseline", gap: 6,
                      padding: "4px 8px", borderRadius: 6, marginBottom: 6,
                      backgroundColor: w.last_temp < 131 ? "#fef2f2" : "#f0fdf4",
                    }}>
                      <span style={{ fontSize: 18, fontWeight: 800, color: w.last_temp < 131 ? "#dc2626" : "#15803d" }}>
                        {w.last_temp}°F
                      </span>
                      <span style={{ fontSize: 10, color: "#999" }}>{timeAgo(w.last_temp_at)}</span>
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 12, fontSize: 11, color: "#666", marginBottom: 4 }}>
                    {w.length && <span>{w.length}' x {w.width || 12}'</span>}
                    {w.cubic_yards && <span>{w.cubic_yards} yd³</span>}
                    {w.tonnage && <span>{w.tonnage} tons</span>}
                  </div>
                  {w.recipe && (
                    <p style={{ fontSize: 11, color: "#888", margin: "0 0 8px", fontStyle: "italic" }}>{w.recipe}</p>
                  )}
                  <div style={{ borderTop: "1px solid #eee", paddingTop: 8, display: "flex", gap: 12 }}>
                    <a
                      href={`/app/windrows/${w.id}`}
                      onClick={(e) => { e.preventDefault(); navigate(`/app/windrows/${w.id}`); }}
                      style={{ fontSize: 12, fontWeight: 600, color: "#2563eb", textDecoration: "none" }}
                    >
                      View Details →
                    </a>
                  </div>
                </div>
              </InfoWindowF>
            );
          })()}
        </GoogleMap>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-5 text-[11px] text-muted-foreground flex-wrap">
        {Object.entries(STATUS_LINE_COLORS).map(([status, color]) => (
          <span key={status} className="flex items-center gap-1.5">
            <span className="w-5 h-1.5 rounded-full" style={{ backgroundColor: color }} />
            {STATUS_CONFIG[status]?.label || status}
          </span>
        ))}
        <span className="flex items-center gap-1.5">
          <span className="w-5 h-1.5 rounded-full" style={{ backgroundColor: STAGING_MAP_COLOR }} />
          Staging
        </span>
        <span className="text-muted-foreground/30">|</span>
        <span className="flex items-center gap-1.5">
          <PileMoundIcon simple size={14} strokeWidth={1.85} className="text-amber-700/95 dark:text-amber-400/95 shrink-0" />
          Piles (area on map)
        </span>
        <span className="text-muted-foreground/30">|</span>
        <span>Hover shape or label to highlight</span>
        <span className="text-muted-foreground/30">|</span>
        <span>View-only -- edit geometry in record details</span>
      </div>
    </div>
  );
}

// ============ MAIN PAGE ============
export default function WindrowsPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [windrows, setWindrows] = useState<Windrow[]>([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const filter = params.get("status") || "all";
  const [search, setSearch] = useState(() => params.get("q") || "");
  const sort = params.get("sort") || "newest";
  const viewMode = params.get("view") === "map" ? "map" : "list";
  const requestedPage = Math.max(1, Number(params.get("page")) || 1);
  const updateView = (key: string, value: string) => setParams(previous => {
    const next = new URLSearchParams(previous);
    value ? next.set(key, value) : next.delete(key);
    if (key !== "page") next.delete("page");
    return next;
  }, { replace: true });
  const setFilter = (value: string) => updateView("status", value);
  const setViewMode = (value: string) => updateView("view", value);
  const [loadError, setLoadError] = useState("");
  const loadGeneration = useRef(0);
  const [showCreate, setShowCreate] = useState(false);
  const [createMode, setCreateMode] = useState<"windrow" | "pile" | "staging">("windrow");
  const [creating, setCreating] = useState(false);
  const isLocalDemo = import.meta.env.VITE_LOCAL_DEMO === "true";
  const [plan, setPlan] = useState(emptyPlan);
  const [createError, setCreateError] = useState("");
  const [form, setForm] = useState({
    rowId: "",
    name: "",
    location: "",
    length: "",
    width: "12",
    cubicYards: "",
    tonnage: "",
    recipe: "",
    notes: "",
    latitude: null as number | null,
    longitude: null as number | null,
    endLatitude: null as number | null,
    endLongitude: null as number | null,
  });
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Windrow | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");

  // Close menu on outside click
  useEffect(() => {
    if (openMenuId === null) return;
    const h = () => setOpenMenuId(null);
    document.addEventListener("click", h);
    return () => document.removeEventListener("click", h);
  }, [openMenuId]);

  const fetchWindrows = useCallback(() => {
    const generation = ++loadGeneration.current;
    setLoadError("");
    setLoading(true);
    apiFetch("/api/windrows", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => { if (!r.ok) throw new Error("Unable to load records. Please try again."); return r.json(); })
      .then((data) => { if (generation === loadGeneration.current) setWindrows(data?.windrows ?? []); })
      .catch(() => { if (generation === loadGeneration.current) setLoadError("Unable to refresh records. Check your connection and try again."); })
      .finally(() => { if (generation === loadGeneration.current) setLoading(false); });
  }, [token]);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await apiFetch(`/api/windrows/${deleteTarget.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Failed (${res.status})`);
      }
      setDeleteTarget(null);
      setDeleteConfirm("");
      fetchWindrows();
    } catch (e: any) {
      alert(e.message || "Failed to delete windrow");
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, token, fetchWindrows]);

  useEffect(() => {
    setWindrows([]);
    fetchWindrows();
    return () => { loadGeneration.current++; };
  }, [fetchWindrows]);

  const handleCreate = async () => {
    if (creating) return;
    if (!form.rowId.trim() || !form.name.trim()) {
      setCreateError("Enter a row ID and name.");
      document.getElementById(!form.rowId.trim() ? "new-row-id" : "new-row-name")?.focus();
      return;
    }
    setCreateError("");
    setCreating(true);
    try {
      if (isLocalDemo) { validateRecipe(plan.recipe); calculateQuantities(plan.measurements); }
      const res = await apiFetch(isLocalDemo ? "/api/local-demo/windrows" : "/api/windrows", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...(isLocalDemo ? {plan} : {}),
          rowId: form.rowId.trim(),
          name: form.name.trim(),
          location: form.location || null,
          length: form.length ? Number(form.length) : null,
          width: form.width ? Number(form.width) : 12,
          cubicYards: form.cubicYards ? Number(form.cubicYards) : null,
          tonnage: form.tonnage ? Number(form.tonnage) : null,
          recipe: form.recipe || null,
          notes: form.notes || null,
          shape: createMode === "windrow" ? "line" : "pile",
          yardRole: createMode === "staging" ? "staging" : "cooking",
          ...(createMode === "staging" ? { color: STAGING_MAP_COLOR } : {}),
          latitude: form.latitude,
          longitude: form.longitude,
          end_latitude: form.endLatitude,
          end_longitude: form.endLongitude,
        }),
      });
      if (res.ok) {
        setShowCreate(false);
        setPlan(emptyPlan());
        setCreateMode("windrow");
        setForm({ rowId: "", name: "", location: "", length: "", width: "12", cubicYards: "", tonnage: "", recipe: "", notes: "", latitude: null, longitude: null, endLatitude: null, endLongitude: null });
        fetchWindrows();
      } else {
        const errData = await res.json().catch(() => null);
        const msg = errData?.error || `Failed to create (${res.status})`;
        setCreateError(msg);
      }
    } catch (err) {
      console.error("Failed to create windrow:", err);
      setCreateError(err instanceof Error ? err.message : "Failed to create windrow. Check your connection.");
    } finally {
      setCreating(false);
    }
  };

  const filtered = windrows.filter(w => (filter === "all" || (filter === "staging" ? (w.yard_role || "cooking") === "staging" : w.status === filter)) &&
    `${w.name} ${w.row_id} ${w.location || ""}`.toLowerCase().includes(search.trim().toLowerCase()))
    .sort((a, b) => sort === "name" ? a.name.localeCompare(b.name, undefined, { numeric: true }) :
      sort === "oldest" ? a.start_date.localeCompare(b.start_date) : b.start_date.localeCompare(a.start_date));
  const pageCount = Math.max(1, Math.ceil(filtered.length / 25));
  const page = Math.min(pageCount, Math.floor(requestedPage));
  const visibleRows = filtered.slice((page - 1) * 25, page * 25);
  const dirty = Boolean(form.rowId || form.name || form.notes || form.location || form.recipe || form.length || form.cubicYards || form.tonnage || form.latitude !== null || JSON.stringify(plan) !== JSON.stringify(emptyPlan()));
  const closeCreate = () => {
    if (creating) return;
    if (dirty && !window.confirm("Close this form? Your entries will stay here until you leave this page.")) return;
    setShowCreate(false);
  };
  useEffect(() => {
    if (!dirty) return;
    const beforeUnload = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [dirty]);
  const activeCount = windrows.filter((w) => w.status === "active").length;
  const alerts = windrows.filter((w) => w.status === "active" && w.last_temp !== null && w.last_temp < 131);
  const totalVolume = windrows.reduce((sum, w) => sum + (w.cubic_yards || 0), 0);
  const totalTonnage = windrows.reduce((sum, w) => sum + (w.tonnage || 0), 0);
  const activeTemps = windrows.filter((w) => w.status === "active" && w.last_temp !== null);
  const avgTemp = activeTemps.length > 0
    ? Math.round(activeTemps.reduce((sum, w) => sum + (w.last_temp || 0), 0) / activeTemps.length)
    : 0;

  return (
    <div className="space-y-4 max-w-6xl">
      {/* Header with view toggle */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="heading-display text-3xl text-foreground">Windrows</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Staging piles hold inbound material. Mix them to a recipe before cooking pads and windrows.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* View toggle */}
          <div className="flex bg-secondary rounded-lg p-0.5">
            <button
              onClick={() => setViewMode("list")}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                viewMode === "list"
                  ? "bg-card text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <List size={13} />
              List
            </button>
            <button
              onClick={() => setViewMode("map")}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                viewMode === "map"
                  ? "bg-card text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Map size={13} />
              Map
            </button>
          </div>

          {isLocalDemo ? <button onClick={() => { setCreateError(""); setCreateMode("windrow"); setPlan(p => ({...p, measurements:{...p.measurements,shape:"triangular"}})); setShowCreate(true); }} className="inline-flex items-center gap-2 px-4 py-2.5 bg-forest-600 hover:bg-forest-700 text-white text-sm font-semibold rounded-xl"><Plus size={16}/>New</button> : (          <div className="flex items-center gap-2">
            <button
              onClick={() => { setCreateMode("windrow"); setShowCreate(true); }}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-forest-600 hover:bg-forest-700 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm shadow-forest-600/20"
            >
              <Plus size={16} strokeWidth={2.5} />
              New Windrow
            </button>
            <button
              onClick={() => { setCreateMode("pile"); setShowCreate(true); }}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-secondary hover:bg-secondary/80 text-foreground border border-border text-sm font-semibold rounded-xl transition-colors"
            >
              <PileMoundIcon size={16} strokeWidth={2.5} className="shrink-0 text-foreground" />
              New Pile
            </button>
            <button
              onClick={() => { setCreateMode("staging"); setShowCreate(true); }}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-secondary hover:bg-secondary/80 text-foreground border border-border text-sm font-semibold rounded-xl transition-colors"
            >
              New Staging
            </button>
          </div>)}

        </div>
      </div>

      {/* Summary strip */}
      {isLocalDemo ? (
        <p className="text-xs text-muted-foreground">{loading ? 'Loading records…' : `${windrows.length} windrows and piles · ${windrows.filter(w=>w.status==='active').length} active`}</p>
      ) : loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 text-xs">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-card border border-border rounded-lg px-3 py-2 animate-pulse">
              <div className="h-2.5 bg-muted rounded w-16 mb-2" />
              <div className="h-5 bg-muted rounded w-12" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 text-xs">
          <div className="bg-card/80 border border-border rounded-lg px-3 py-2 hover:border-forest-300 dark:hover:border-forest-800 transition-colors">
            <div className="flex items-center justify-between mb-2">
              <span className="label-caps text-muted-foreground">Active Rows</span>
              <div className="w-7 h-7 rounded-md bg-forest-100 dark:bg-forest-900/30 flex items-center justify-center">
                <Layers size={14} className="text-forest-600 dark:text-forest-400" />
              </div>
            </div>
            <p className="font-semibold text-lg text-foreground leading-tight">{activeCount}</p>
            <p className="text-[10px] text-muted-foreground mt-0.5">{windrows.length} total</p>
          </div>
          <div className="bg-card/80 border border-border rounded-lg px-3 py-2 hover:border-amber-300 dark:hover:border-amber-800 transition-colors">
            <div className="flex items-center justify-between mb-2">
              <span className="label-caps text-muted-foreground">Total Volume</span>
              <div className="w-7 h-7 rounded-md bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <Gauge size={14} className="text-amber-600 dark:text-amber-400" />
              </div>
            </div>
            <p className="font-semibold text-lg text-foreground leading-tight">
              {Math.round(totalVolume)}
              <span className="text-[11px] text-muted-foreground ml-0.5"> yd³</span>
            </p>
            {totalTonnage > 0 && <p className="text-[10px] text-muted-foreground mt-0.5">{totalTonnage.toFixed(1)} tons</p>}
          </div>
          <div className="bg-card/80 border border-border rounded-lg px-3 py-2 hover:border-amber-300 dark:hover:border-amber-800 transition-colors">
            <div className="flex items-center justify-between mb-2">
              <span className="label-caps text-muted-foreground">Avg Temperature</span>
              <div className="w-7 h-7 rounded-md bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <Flame size={14} className="text-amber-600 dark:text-amber-400" />
              </div>
            </div>
            <p className="font-semibold text-lg text-foreground leading-tight">
              {avgTemp > 0 ? (
                <>
                  {avgTemp}
                  <span className="text-[11px] text-muted-foreground ml-0.5">°F</span>
                </>
              ) : (
                "--"
              )}
            </p>
            <p className="text-[10px] text-muted-foreground mt-0.5">across active rows</p>
          </div>
          <div
            className={`bg-card/80 border rounded-lg px-3 py-2 transition-colors ${
            alerts.length > 0
              ? "border-red-200 dark:border-red-900 hover:border-red-300 dark:hover:border-red-800"
              : "border-border hover:border-forest-300 dark:hover:border-forest-800"
          }`}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="label-caps text-muted-foreground">Temp Alerts</span>
              <div className={`w-7 h-7 rounded-md flex items-center justify-center ${
                alerts.length > 0
                  ? "bg-red-100 dark:bg-red-900/30"
                  : "bg-forest-100 dark:bg-forest-900/30"
              }`}>
              <AlertTriangle size={14} className={alerts.length > 0 ? "text-red-600 dark:text-red-400" : "text-forest-600 dark:text-forest-400"} />
              </div>
            </div>
            <p className={`font-semibold text-lg leading-tight ${alerts.length > 0 ? "text-red-600 dark:text-red-400" : "text-foreground"}`}>
              {alerts.length}
            </p>
            <p className="text-[10px] text-muted-foreground mt-0.5">below 131°F</p>
          </div>
        </div>
      )}

      {loadError && <div role="alert" className="flex items-center justify-between gap-3 p-3 border border-red-300 rounded-lg text-sm"><span>{loadError}</span><button onClick={fetchWindrows} className="min-h-11 px-3 font-semibold">Retry</button></div>}
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex-1 min-w-[180px]"><span className="sr-only">Search windrows by name, ID or location</span><input type="search" value={search} onChange={e => { setSearch(e.target.value); updateView("q", e.target.value); }} placeholder="Search name, ID or location" className="w-full min-h-11 rounded-lg border border-border bg-background px-3 text-sm" /></label>
        <label className="flex items-center gap-2 text-sm">Sort<select value={sort} onChange={e => updateView("sort", e.target.value)} className="min-h-11 rounded-lg border border-border bg-background px-3"><option value="newest">Newest first</option><option value="oldest">Oldest first</option><option value="name">Name A–Z</option></select></label>
      </div>
      {/* Filter tabs */}
      <div className="flex gap-1.5 flex-wrap">
        {[
          { key: "all", label: "All", count: windrows.length },
          { key: "staging", label: "Staging", count: windrows.filter(w => (w.yard_role || "cooking") === "staging").length },
          { key: "active", label: "Active", count: windrows.filter(w => w.status === "active").length },
          { key: "cooking_complete", label: "Complete", count: windrows.filter(w => w.status === "cooking_complete").length },
          { key: "ready_for_pickup", label: "Ready", count: windrows.filter(w => w.status === "ready_for_pickup").length },
          { key: "removed", label: "Removed", count: windrows.filter(w => w.status === "removed").length },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            aria-pressed={filter === tab.key}
            className={`min-h-11 px-3.5 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
              filter === tab.key
                ? "bg-forest-600 text-white shadow-sm"
                : "bg-secondary text-muted-foreground hover:text-foreground"
            }`}
          >
            {tab.label}
            {tab.count > 0 && (
              <span className={`ml-1.5 ${filter === tab.key ? "text-white/70" : "text-muted-foreground/60"}`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Content: List or Map view */}
      {viewMode === "map" ? (
        <WindrowSiteMap windrows={filtered} token={token!} onRefresh={fetchWindrows} />
      ) : loading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse rounded-lg border border-border bg-card p-4">
              <div className="mb-2 h-4 w-40 rounded bg-muted" />
              <div className="h-3 w-64 rounded bg-muted" />
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-16 text-center">
          <Layers size={40} className="mx-auto text-muted-foreground/30 mb-3" />
          <p className="text-sm text-muted-foreground">
            {loadError ? "Records are unavailable. Retry above." : search ? "No matching records. Try a different search or status." : filter === "all"
              ? "No windrows or piles yet. Create your first record."
              : filter === "staging"
              ? "No staging piles yet. Create a receiving pile to hold inbound material."
              : `No ${filter.replace(/_/g, " ")} rows or piles.`}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {visibleRows.map((windrow) => {
            const statusConfig = STATUS_CONFIG[windrow.status] || STATUS_CONFIG.active;
            const statusDot = STATUS_INDICATOR_DOT[windrow.status] || STATUS_INDICATOR_DOT.active;
            const tempAlert = windrow.status === "active" && windrow.last_temp !== null && windrow.last_temp < 131;
            const isPile = windrow.shape === "pile";
            const isStaging = (windrow.yard_role || "cooking") === "staging";
            const typeLabel = isStaging ? "Staging" : isPile ? "Pile" : "Windrow";

            return (
              <Link
                key={windrow.id}
                to={`/app/windrows/${windrow.id}`}
                aria-label={`${windrow.name}, ${windrow.row_id}, ${typeLabel}, ${isStaging ? "staging" : statusConfig.label}`}
                className={`group block rounded-lg border bg-card transition-colors ${
                  tempAlert
                    ? "border-red-200/90 dark:border-red-900/80"
                    : "border-border hover:bg-muted/25 dark:hover:bg-muted/10"
                }`}
              >
                <div className="flex items-center gap-4 p-4">
                  <div
                    className={`flex flex-col items-center justify-center gap-1 shrink-0 rounded-md border px-2 py-1.5 min-w-[3.5rem] ${
                      isStaging
                        ? "border-earth-500/40 bg-earth-500/10"
                        : tempAlert
                        ? "border-red-200/80 bg-red-50/50 dark:border-red-900/50 dark:bg-red-950/20"
                        : "border-border/80 bg-background"
                    }`}
                    aria-hidden
                  >
                    {isPile || isStaging ? (
                      <PileMoundIcon
                        simple
                        size={20}
                        strokeWidth={2}
                        className={isStaging ? "text-earth-500" : tempAlert ? "text-red-600 dark:text-red-400" : "text-muted-foreground"}
                      />
                    ) : (
                      <WindrowStripeIcon
                        simple
                        size={20}
                        className={tempAlert ? "text-red-600 dark:text-red-400" : "text-muted-foreground"}
                      />
                    )}
                    <span
                      className={`text-[10px] font-semibold uppercase tracking-wide leading-none ${
                        isStaging ? "text-earth-500" : tempAlert ? "text-red-600 dark:text-red-400" : "text-muted-foreground"
                      }`}
                    >
                      {typeLabel}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                      <h3 className="text-sm font-semibold text-foreground tracking-tight">{windrow.name}</h3>
                      <span className="font-mono text-xs text-muted-foreground tabular-nums">{windrow.row_id}</span>
                    </div>
                    <p className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs leading-relaxed text-muted-foreground">
                      <span className="inline-flex items-center gap-1.5">
                        <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${statusDot}`} aria-hidden />
                        <span className="font-medium text-foreground/80">{isStaging ? yardRoleLabel("staging") : statusConfig.label}</span>
                      </span>
                      {windrow.location && (
                        <>
                          <span className="text-muted-foreground/30" aria-hidden>
                            ·
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <MapPin className="h-3 w-3 shrink-0 opacity-70" aria-hidden />
                            {windrow.location}
                          </span>
                        </>
                      )}
                      <span className="text-muted-foreground/30" aria-hidden>
                        ·
                      </span>
                      <span>
                        Started{" "}
                        {new Date(windrow.start_date).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </span>
                      {windrow.recipe && (
                        <>
                          <span className="text-muted-foreground/30" aria-hidden>
                            ·
                          </span>
                          <span>{windrow.recipe}</span>
                        </>
                      )}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-5">
                    <div className="hidden text-right sm:block">
                      {windrow.last_temp !== null ? (
                        <>
                          <p
                            className={`text-base font-semibold tabular-nums tracking-tight ${
                              tempAlert ? "text-red-600 dark:text-red-400" : "text-foreground"
                            }`}
                          >
                            {windrow.last_temp}°F
                          </p>
                          <p className="text-[11px] text-muted-foreground">{timeAgo(windrow.last_temp_at)}</p>
                        </>
                      ) : (
                        <p className="text-xs text-muted-foreground">No readings</p>
                      )}
                    </div>
                    <div className="hidden items-center gap-4 text-xs tabular-nums text-muted-foreground md:flex">
                      <span className="flex items-center gap-1">
                        <Thermometer size={12} className="opacity-60" aria-hidden />
                        {windrow.temp_count}
                      </span>
                      <span className="flex items-center gap-1">
                        <RotateCcw size={12} className="opacity-60" aria-hidden />
                        {windrow.turn_count}
                      </span>
                    </div>
                    {/* Overflow menu — low-profile, needs intentional click */}
                    <div className="relative" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setOpenMenuId(openMenuId === windrow.id ? null : windrow.id);
                        }}
                        className="p-1.5 rounded-md text-muted-foreground/50 hover:text-foreground hover:bg-muted transition-colors"
                        aria-label="More options"
                      >
                        <MoreVertical size={16} />
                      </button>
                      {openMenuId === windrow.id && (
                        <div className="absolute right-0 top-full mt-1 w-44 bg-card border border-border rounded-lg shadow-lg z-30 overflow-hidden">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setOpenMenuId(null);
                              navigate(`/app/windrows/${windrow.id}`);
                            }}
                            className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                          >
                            View details
                          </button>
                          <div className="border-t border-border" />
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setOpenMenuId(null);
                              setDeleteTarget(windrow);
                              setDeleteConfirm("");
                            }}
                            className="w-full text-left px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors flex items-center gap-2"
                          >
                            <Trash2 size={13} /> Delete windrow
                          </button>
                        </div>
                      )}
                    </div>
                    <ChevronRight size={16} className="text-muted-foreground/35" aria-hidden />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => { if (!deleting) { setDeleteTarget(null); setDeleteConfirm(""); } }}
        >
          <div
            className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                <Trash2 size={18} className="text-red-600 dark:text-red-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-bold text-foreground">Delete this windrow?</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  This will hide <strong className="text-foreground">{deleteTarget.name}</strong> ({deleteTarget.row_id}) from the list. All temperatures, turns, and materials stay safe in the database and can be restored by an admin.
                </p>
              </div>
            </div>
            <div className="my-4">
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Type <span className="font-mono text-foreground">delete</span> to confirm
              </label>
              <input
                type="text"
                autoFocus
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                placeholder="delete"
                className="w-full mt-2 px-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500/40 focus:border-red-500"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => { setDeleteTarget(null); setDeleteConfirm(""); }}
                disabled={deleting}
                className="px-4 py-2 text-sm font-semibold rounded-lg bg-secondary text-foreground hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting || deleteConfirm.trim().toLowerCase() !== "delete"}
                className="px-4 py-2 text-sm font-semibold rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {deleting ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {viewMode === "list" && filtered.length > 0 && <nav aria-label="Windrow pages" className="flex items-center justify-between gap-2 text-sm"><span aria-live="polite">{(page - 1) * 25 + 1}–{Math.min(page * 25, filtered.length)} of {filtered.length}</span><div className="flex items-center gap-3"><button disabled={page === 1} onClick={() => updateView("page", String(page - 1))} className="min-h-11 px-3 rounded-lg border border-border disabled:opacity-40">Previous</button><span>{page} / {pageCount}</span><button disabled={page === pageCount} onClick={() => updateView("page", String(page + 1))} className="min-h-11 px-3 rounded-lg border border-border disabled:opacity-40">Next</button></div></nav>}

      {/* Create windrow -- full page for more space and line-on-map */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex flex-col bg-background">
          <div className="flex items-center justify-between shrink-0 px-4 md:px-6 py-3 border-b border-border bg-card">
            <div>
              <h2 className="heading-display text-xl text-foreground">
                {createMode === "staging" ? "Create Staging Pile" : createMode === "pile" ? "Create New Pile" : "Create New Windrow"}
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                {createMode === "staging"
                  ? "Receiving pile for inbound material. Mix it to a recipe before it goes onto a cooking pad."
                  : isLocalDemo ? "Choose a type, estimate quantities and build its recipe." : createMode === "pile" ? "Set up a new compost pile (circle on map)" : "Set up a new composting windrow (line on map)"}
              </p>
            </div>
            <button disabled={creating} onClick={closeCreate} className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors" aria-label="Close">
              <X size={20} />
            </button>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto">
            <div className="max-w-4xl mx-auto p-6 md:p-8 space-y-6">
              {isLocalDemo && <fieldset className="space-y-2"><legend className="text-sm font-semibold mb-2">What are you creating?</legend><div className="flex flex-wrap gap-2">{(["windrow","pile","staging"] as const).map(mode=><button key={mode} type="button" aria-pressed={createMode===mode} onClick={()=>{setCreateMode(mode);setPlan({...plan,measurements:{...plan.measurements,shape:mode==="windrow"?"triangular":"cone"}});}} className={`px-5 py-2.5 rounded-xl border text-sm font-semibold ${createMode===mode?"bg-forest-600 text-white border-forest-600":"border-border bg-card text-muted-foreground"}`}>{mode==="staging"?"Staging pile":mode==="pile"?"Pile":"Windrow"}</button>)}</div></fieldset>}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="new-row-id" className="label-caps text-muted-foreground mb-2 block">Row ID *</label>
                  <input
                    type="text"
                    id="new-row-id" required aria-describedby={createError ? "create-error" : undefined} placeholder="e.g. A-12"
                    value={form.rowId}
                    onChange={(e) => setForm({ ...form, rowId: e.target.value })}
                    className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-forest-500/50 focus:border-forest-500"
                  />
                </div>
                <div>
                  <label htmlFor="new-row-name" className="label-caps text-muted-foreground mb-2 block">Name *</label>
                  <input
                    type="text"
                    id="new-row-name" required aria-describedby={createError ? "create-error" : undefined} placeholder="e.g. Windrow A-12"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-forest-500/50 focus:border-forest-500"
                  />
                </div>
              </div>
              <div>
                <label className="label-caps text-muted-foreground mb-2 block">Location</label>
                <input
                  type="text"
                  placeholder="e.g. Receiving pad, Pad A"
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-forest-500/50 focus:border-forest-500"
                />
              </div>
              {!isLocalDemo && <>
              <Suspense fallback={<div className="h-[280px] bg-muted rounded-xl animate-pulse" />}>
                {createMode !== "windrow" ? (
                  <WindrowCirclePicker
                    latitude={form.latitude}
                    longitude={form.longitude}
                    endLatitude={form.endLatitude}
                    endLongitude={form.endLongitude}
                    onCircleChange={(centerLat, centerLng, edgeLat, edgeLng) =>
                      setForm({ ...form, latitude: centerLat, longitude: centerLng, endLatitude: edgeLat, endLongitude: edgeLng })
                    }
                    otherWindrows={windrows.map((w) => ({
                      id: w.id,
                      name: w.name,
                      latitude: w.latitude,
                      longitude: w.longitude,
                      end_latitude: w.end_latitude,
                      end_longitude: w.end_longitude,
                      shape: w.shape,
                    }))}
                    editingWindrowId={-1}
                  />
                ) : (
                  <WindrowLinePicker
                    latitude={form.latitude}
                    longitude={form.longitude}
                    endLatitude={form.endLatitude}
                    endLongitude={form.endLongitude}
                    onLineChange={(lat, lng, endLat, endLng) =>
                      setForm({ ...form, latitude: lat, longitude: lng, endLatitude: endLat, endLongitude: endLng })
                    }
                    otherWindrows={windrows.map((w) => ({
                      id: w.id,
                      name: w.name,
                      latitude: w.latitude,
                      longitude: w.longitude,
                      end_latitude: w.end_latitude,
                      end_longitude: w.end_longitude,
                    }))}
                    editingWindrowId={-1}
                  />
                )}
              </Suspense>
              </>}
              {isLocalDemo ? <RecipePlanEditor value={plan} onChange={setPlan} pile={createMode!=="windrow"}/> : <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="label-caps text-muted-foreground mb-2 block">Length (ft)</label>
                  <input
                    type="number"
                    value={form.length}
                    onChange={(e) => setForm({ ...form, length: e.target.value })}
                    className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-forest-500/50 focus:border-forest-500"
                  />
                </div>
                <div>
                  <label className="label-caps text-muted-foreground mb-2 block">Width (ft)</label>
                  <input
                    type="number"
                    value={form.width}
                    onChange={(e) => setForm({ ...form, width: e.target.value })}
                    className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-forest-500/50 focus:border-forest-500"
                  />
                </div>
                <div>
                  <label className="label-caps text-muted-foreground mb-2 block">Volume (yd³)</label>
                  <input
                    type="number"
                    value={form.cubicYards}
                    onChange={(e) => setForm({ ...form, cubicYards: e.target.value })}
                    className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-forest-500/50 focus:border-forest-500"
                  />
                </div>
                <div>
                  <label className="label-caps text-muted-foreground mb-2 block">Tonnage</label>
                  <input
                    type="number"
                    step="0.1"
                    value={form.tonnage}
                    onChange={(e) => setForm({ ...form, tonnage: e.target.value })}
                    placeholder="tons"
                    className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-forest-500/50 focus:border-forest-500"
                  />
                </div>
              </div>
              <div>
                <label className="label-caps text-muted-foreground mb-2 block">Recipe</label>
                <input
                  type="text"
                  placeholder="e.g. Green waste + Food waste 3:1"
                  value={form.recipe}
                  onChange={(e) => setForm({ ...form, recipe: e.target.value })}
                  className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-forest-500/50 focus:border-forest-500"
                />
              </div>
              </>}
              {isLocalDemo && <details className="border-t border-border pt-4"><summary className="cursor-pointer text-sm font-semibold text-foreground mb-3">Place on the map · optional</summary>
              <Suspense fallback={<div className="h-[280px] bg-muted rounded-xl animate-pulse" />}>
                {createMode !== "windrow" ? (
                  <WindrowCirclePicker
                    latitude={form.latitude}
                    longitude={form.longitude}
                    endLatitude={form.endLatitude}
                    endLongitude={form.endLongitude}
                    onCircleChange={(centerLat, centerLng, edgeLat, edgeLng) =>
                      setForm({ ...form, latitude: centerLat, longitude: centerLng, endLatitude: edgeLat, endLongitude: edgeLng })
                    }
                    otherWindrows={windrows.map((w) => ({
                      id: w.id,
                      name: w.name,
                      latitude: w.latitude,
                      longitude: w.longitude,
                      end_latitude: w.end_latitude,
                      end_longitude: w.end_longitude,
                      shape: w.shape,
                    }))}
                    editingWindrowId={-1}
                  />
                ) : (
                  <WindrowLinePicker
                    latitude={form.latitude}
                    longitude={form.longitude}
                    endLatitude={form.endLatitude}
                    endLongitude={form.endLongitude}
                    onLineChange={(lat, lng, endLat, endLng) =>
                      setForm({ ...form, latitude: lat, longitude: lng, endLatitude: endLat, endLongitude: endLng })
                    }
                    otherWindrows={windrows.map((w) => ({
                      id: w.id,
                      name: w.name,
                      latitude: w.latitude,
                      longitude: w.longitude,
                      end_latitude: w.end_latitude,
                      end_longitude: w.end_longitude,
                    }))}
                    editingWindrowId={-1}
                  />
                )}
              </Suspense>
              </details>}
              <div>
                <label className="label-caps text-muted-foreground mb-2 block">Notes</label>
                <textarea
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-forest-500/50 focus:border-forest-500 resize-none"
                />
              </div>
              {createError && <p id="create-error" role="alert" className="text-sm text-red-600">{createError}</p>}
              <div className="flex flex-wrap gap-3 pt-4 border-t border-border">
                <button
                  disabled={creating} onClick={closeCreate}
                  className="min-w-[100px] px-5 py-3 bg-secondary hover:bg-secondary/80 text-foreground rounded-xl text-sm font-semibold transition-colors border border-border"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreate}
                  disabled={creating}
                  className="min-w-[140px] px-5 py-3 bg-forest-600 hover:bg-forest-700 disabled:opacity-50 text-white rounded-xl text-sm font-semibold transition-colors"
                >
                  {creating ? "Creating..." : createMode === "staging" ? "Create Staging Pile" : createMode === "pile" ? "Create Pile" : "Create Windrow"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
